import { Link } from 'react-router-dom';
import { Award, Users } from 'lucide-react';
import { Badge } from '@/components/shared/Badge';
import { GlassCard } from '@/components/shared/GlassCard';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { useCompanionDeck } from '@/features/marketplace/hooks/useCompanions';

const TIER_PROGRESS = 0.62;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatTripDates(dateFrom: string, dateTo: string): string {
  // dateFrom e.g. "2026-05-19", dateTo e.g. "2026-05-25" → "May 19–25"
  const [, fromMonth, fromDay] = dateFrom.split('-');
  const [, , toDay] = dateTo.split('-');
  return `${MONTHS[parseInt(fromMonth, 10) - 1]} ${parseInt(fromDay, 10)}–${parseInt(toDay, 10)}`;
}

export function RewardsCompanions() {
  const { data: companionDeck = [] } = useCompanionDeck();
  const companions = companionDeck.slice(0, 3);

  return (
    <section className="mx-auto mt-24 grid max-w-7xl grid-cols-1 gap-4 px-4 md:px-6 lg:grid-cols-[1.3fr_1fr]">
      {/* Rewards card */}
      <Link to="/rewards" className="block">
        <GlassCard interactive className="relative overflow-hidden p-7 md:p-8">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-16 h-[280px] w-[280px] rounded-full"
            style={{ background: `radial-gradient(${brand.teal}50, transparent 70%)` }}
          />
          <div className="relative">
            <Badge color={brand.teal} icon={Award}>
              Adventurer · Tier 2
            </Badge>
            <h3 className="font-display mt-4 text-3xl font-semibold leading-tight tracking-tight text-ink md:text-4xl">
              760 points to <em style={{ color: brand.saffron }}>Summit.</em>
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-ink-dim">
              At Summit: 15% off houseboats, free gear hire on 5+ day treks, annual priority pass
              for Gulmarg Gondola.
            </p>

            <div className="mt-6">
              <div className="mb-2 flex justify-between text-xs text-ink-dim">
                <span>Adventurer · 1,240</span>
                <span className="font-semibold" style={{ color: brand.saffron }}>
                  Summit · 2,000
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full border border-[var(--glass-edge)] bg-white/5">
                <div
                  className="h-full transition-[width] duration-700"
                  style={{
                    width: `${TIER_PROGRESS * 100}%`,
                    background: `linear-gradient(90deg, ${brand.teal}, ${brand.saffron})`,
                    boxShadow: `0 0 16px var(--color-teal-glow)`,
                  }}
                />
              </div>
            </div>
          </div>
        </GlassCard>
      </Link>

      {/* Companions card */}
      <Link to="/community" className="block">
        <GlassCard interactive className="p-6">
          <Badge color={brand.chinar} icon={Users}>
            Companions
          </Badge>
          <h3 className="font-display mt-4 text-xl font-semibold leading-tight tracking-tight text-ink md:text-2xl">
            {companions.length} trekker{companions.length !== 1 ? 's' : ''} looking for trail buddies.
          </h3>
          <ul className="mt-4 flex flex-col gap-2">
            {companions.length === 0 ? (
              <li className="flex items-center gap-3 rounded-xl border border-[var(--glass-edge)] glass p-2 animate-pulse">
                <div className="h-8 w-8 shrink-0 rounded-full bg-white/10" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="h-2.5 w-24 rounded bg-white/10" />
                  <div className="h-2 w-16 rounded bg-white/5" />
                </div>
              </li>
            ) : (
              companions.map((c) => {
                const trip = c.upcoming_trips?.[0];
                const dates = trip
                  ? formatTripDates(trip.date_from, trip.date_to)
                  : '';
                const desc = `${c.location ?? 'Traveller'} · ${c.fitness_level}`;
                const imgSrc = c.avatar_url ?? unsplash(photo.guideM1, { w: 100 });

                return (
                  <li
                    key={c.id}
                    className="flex items-center gap-3 rounded-xl border border-[var(--glass-edge)] glass p-2"
                  >
                    <img
                      src={imgSrc}
                      alt=""
                      aria-hidden
                      className="h-8 w-8 shrink-0 rounded-full object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] font-semibold text-ink">{c.name}</div>
                      <div className="text-[11px] text-ink-mute">{desc}</div>
                    </div>
                    {dates && (
                      <div
                        className="text-[10px] font-semibold"
                        style={{ color: brand.teal }}
                      >
                        {dates}
                      </div>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </GlassCard>
      </Link>
    </section>
  );
}
